import { Badge, Group, Paper, SegmentedControl, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { formatDateTime, formatRelative } from '../../lib/format';
import { AttentionCell, MoneyThatDoesNotAddUp, WhatTheyAskedForButton } from './WhatTheyAskedFor';

export const PLATFORM_ORDERS_PATH = '/admin/v1/channels/platform-orders';
const CHANNELS_PATH = '/admin/v1/channels/channels';

interface PlatformOrder {
  orderId?: string;
  channelId?: string;
  receivedAt?: string;
  lineCount?: number;
  unmatchedLineCount?: number;
  /** Why this order wants a look — see `WhatTheyAskedFor`. Absent on an ordinary one. */
  attentionFlags?: string[] | null;
  unexplainedMinor?: number | null;
}

interface Channel {
  id?: string;
  name?: string;
  type?: string;
  active?: boolean;
  platformMerchantId?: string | null;
  webhookEnabled?: boolean;
}

/**
 * Whether each platform is actually plugged in, above the list of what has arrived.
 *
 * An empty list is the one thing this screen cannot explain by itself. Orders stop for two
 * completely different reasons — the account number on the channel does not match the one the
 * platform is sending, or the switch is off — and both look identical from here: nothing. Worse,
 * the first of those is silent by design: an order whose account number we do not recognise
 * belongs to another restaurant and is thrown away without a trace, exactly as it should be.
 *
 * So the setup is stated rather than inferred, and beside it the last time anything came in. "Set
 * up, switched on, nothing since Tuesday" is a question for the platform; "no account number" is
 * twenty seconds of work on the tab next door.
 */
function PlatformStatus({ orders }: { orders: PlatformOrder[] }) {
  const query = useQuery({
    queryKey: listKey(CHANNELS_PATH),
    queryFn: () => rawApi<Channel[]>('GET', CHANNELS_PATH),
  });

  const platforms = (query.data ?? []).filter((c) => c.type === 'deliveryPlatform' && c.active !== false);
  if (platforms.length === 0) return null;

  const lastByChannel = new Map<string, string>();
  for (const order of orders) {
    const channelId = order.channelId ?? '';
    const at = order.receivedAt ?? '';
    if (!at) continue;
    const seen = lastByChannel.get(channelId);
    if (!seen || at > seen) lastByChannel.set(channelId, at);
  }

  return (
    <Group gap="sm" mb="md" align="stretch">
      {platforms.map((platform) => {
        const last = lastByChannel.get(platform.id ?? '');
        return (
          // Capped as well as floored, so the platform with the longest explanation on it does not
          // stretch to the full width and push every other platform onto a line of its own.
          <Paper key={platform.id} withBorder p="sm" radius="md" miw={260} maw={320}>
            <Group justify="space-between" wrap="nowrap" gap="xs" mb={6}>
              <Text fw={600}>{platform.name}</Text>
              {!platform.platformMerchantId ? (
                <Badge color="gray" variant="light">
                  Not set up
                </Badge>
              ) : platform.webhookEnabled ? (
                <Badge color="teal" variant="light">
                  Switched on
                </Badge>
              ) : (
                <Badge color="orange" variant="light">
                  Switched off
                </Badge>
              )}
            </Group>
            {!platform.platformMerchantId ? (
              <Text size="sm" c="dimmed">
                Nothing can arrive until their account number for this bar is on the channel. Add it under
                “Where sales come from”.
              </Text>
            ) : !platform.webhookEnabled ? (
              <Text size="sm" c="dimmed">
                Their orders are being turned away on purpose. Switch them back on under “Where sales come
                from” when their end is behaving.
              </Text>
            ) : last ? (
              <Stack gap={0}>
                <Text size="sm">Last order {formatRelative(last)}</Text>
                <Text size="xs" c="dimmed">
                  {formatDateTime(last)}
                </Text>
              </Stack>
            ) : (
              // Phrased against the list rather than as "nothing has ever arrived", which this card
              // is not in a position to say: it reads the same capped page of recent orders the
              // table below shows, so a quiet platform sitting behind a busy one would be declared
              // dead when it is only further down.
              <Text size="sm" c="dimmed">
                Set up and switched on. Nothing from them among the orders below.
              </Text>
            )}
          </Paper>
        );
      })}
    </Group>
  );
}

/** How many of an order's lines the menu could not account for, in the words that say what to do. */
function ItemsCell(row: Record<string, unknown>) {
  const lines = typeof row.lineCount === 'number' ? row.lineCount : 0;
  const unmatched = typeof row.unmatchedLineCount === 'number' ? row.unmatchedLineCount : 0;
  if (unmatched === 0) {
    return (
      <Text size="sm">
        {lines} {lines === 1 ? 'item' : 'items'}
      </Text>
    );
  }
  // Orange rather than red: nothing is lost — the sale is recorded and the platform still owes for
  // it — but the check is short until the name is matched, which is work for a person.
  return (
    <Text size="sm" c="orange" fw={500}>
      {unmatched} of {lines} not on your menu
    </Text>
  );
}

/**
 * Orders the delivery platform sent us, newest first.
 *
 * This tab exists because until it did, an order arriving from a platform was invisible: it became
 * a receivable on the "Owed to you" tab with no way to see what it was for, and an order whose
 * items nobody had matched rang up short with nothing anywhere saying so. The owner would have
 * found out at the payout, weeks later, against a statement that no longer matched anything.
 */
export function OrdersArriving() {
  const [filter, setFilter] = useState<'all' | 'attention'>('all');

  const orders = useQuery({
    queryKey: listKey(PLATFORM_ORDERS_PATH),
    queryFn: () => rawApi<PlatformOrder[]>('GET', PLATFORM_ORDERS_PATH),
  });
  const needingAttention = (orders.data ?? []).filter((o) => (o.unmatchedLineCount ?? 0) > 0).length;

  return (
    <>
      <PlatformStatus orders={orders.data ?? []} />
      {/* Above the table, because it is money and because the badge that says so is in a column a
          reader has to get to. An order can be charged for something no item on it accounts for —
          a paid extra the guest chose — and only the items go on the platform's bill. */}
      <MoneyThatDoesNotAddUp orders={(orders.data ?? []) as Record<string, unknown>[]} />
      <CrudSection
        title="Orders arriving"
        description="Every order a delivery platform has sent us, as it arrived. An order is put on the books the moment it lands — you do not enter these by hand."
        listPath={PLATFORM_ORDERS_PATH}
        // Only sent when it is on. `needsMatching=false` in the address does NOT mean false — any
        // value at all reads as yes — so the way to ask for everything is not to ask.
        listQuery={filter === 'attention' ? { needsMatching: 1 } : undefined}
        // A summary row is keyed by the order it summarises. Without this every row arrives with no
        // `id`, the table gives them all the same key, and re-sorting or filtering leaves cells
        // from one order sitting under another order's heading.
        extractList={(data) =>
          (Array.isArray(data) ? (data as Record<string, unknown>[]) : []).map((row) => ({
            ...row,
            id: row.orderId,
          }))
        }
        emptyMessage={
          filter === 'attention'
            ? 'Every order so far rang up in full — nothing is waiting to be matched.'
            : 'Nothing has arrived yet. The cards above say whether the platform is set up to send it.'
        }
        actions={
          <SegmentedControl
            size="xs"
            value={filter}
            onChange={(value) => setFilter(value as 'all' | 'attention')}
            data={[
              { value: 'all', label: 'All orders' },
              {
                value: 'attention',
                // The count is on the control because it is the reason to press it. A tab that
                // says "Needs matching" and then shows an empty table is a tab you stop trusting.
                label: needingAttention > 0 ? `Needs matching (${needingAttention})` : 'Needs matching',
              },
            ]}
          />
        }
        // Only on the orders that carried something. A button on every row saying "nothing to see"
        // on almost all of them is a button nobody presses on the day there is.
        rowActions={(row) => <WhatTheyAskedForButton row={row} />}
        columns={[
          { accessor: 'receivedAt', title: 'Arrived', unit: 'datetime', noWrap: true },
          { accessor: 'channelId', title: 'Platform', reference: 'channel' },
          { accessor: 'platformOrderNumber', title: 'Their order no.' },
          { accessor: 'customerName', title: 'Customer' },
          { accessor: 'lineCount', title: 'Items', render: ItemsCell },
          // What the PLATFORM owes for the food, which is not what the guest paid: the delivery fee
          // on top of it is theirs. Titled so nobody reconciles it against a guest's receipt.
          { accessor: 'grossMinor', title: 'Owed to you', unit: 'money' },
          { accessor: 'status', title: 'Where it got to', enumGroup: 'platformOrderStatus' },
          {
            accessor: 'signatureVerified',
            title: 'Order source',
            render: (row) => (
              <Badge color={row.signatureVerified === true ? 'teal' : 'gray'} variant="light">
                {row.signatureVerified === true
                  ? 'Verified'
                  : row.signatureVerified === false
                    ? 'Not verified'
                    : 'Not recorded'}
              </Badge>
            ),
          },
          // The last column and usually empty, which is the point: an order that carried a chosen
          // option, a note for the kitchen, or money the items do not account for says so here
          // instead of landing as an ordinary line with the rest sitting unread in a stored file.
          {
            accessor: 'attentionFlags',
            title: 'Anything else?',
            render: (row) => <AttentionCell row={row} />,
          },
        ]}
      />
    </>
  );
}
